/* eslint-disable */
import Promise from 'bluebird';
import _ from 'underscore';
import { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, WIZARD_EVENTS, getWizardData } from '@bingads-webui-react/wizard';
import {
  loadBundleCountries,
  getErrorsPlaceholders,
  readTargetingCriterions as readAdGroupTargetingCriterions,
  readAudienceCriterions as readAdGroupAudienceCriterions,
  readLocationCriterions as readAdGroupLocationCriterions,
  modelToViewModelForLocationTargets,
  AdGroupNegativeReferenceId,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { isAudienceShoppingCampaign } from '@bingads-webui-campaign-react/constants';
import { defaultBidForShoppingCampaign } from '@bingads-webui-campaign-legacy/budget/shared/defaultBid';

import {
  readTargetingCriterions,
  readDayTimeCriterions,
  readLocationCriterions,
  readAudienceCriterions
} from '../../model/campaign-criterions';
import { allSettledPromiseWrapper, isSearchShoppingCampaign } from '../step-utils';
import { useWizardLog, LOG_STATUS } from '../../shared';
import {
  loadCampaign,
  modelToViewModelForAdGroup,
  modelToViewModelForCampaign,
  viewModelToModelForCampaign,
  viewModelToModelForConversionGoals,
  viewModelToBulkModelForAdSchedules,
  saveCampaign,
  saveAdGroup,
  convertMachineDateToAccountDate,
} from '../../model';
import { CREATE_SCENARIO } from '../../constants';

const getAdGroup = ({
  campaign,
  locationCriterionsModel,
  bundleCountries,
}) => {
  const isSearch = !isAudienceShoppingCampaign(campaign.CampaignType, campaign.CampaignSubType);
  const adgroup = {
    CampaignName: campaign.Name,
    CampaignSubType: campaign.CampaignSubType,
    CampaignBidStrategyName: campaign.BiddingScheme && campaign.BiddingScheme.Type,
    Name: 'Ad group #1',
    PricingModel: 'CPC',
    Medium: 'Search',
    Status: 'Paused', // Keep the ad group paused before go live so that we can update start date
    StartDate: convertMachineDateToAccountDate(new Date()), // Always use account timezone
    SearchBid: defaultBidForShoppingCampaign(),
    adSchedules: {
      dayTimeRangs: [],
    },
    ...modelToViewModelForLocationTargets({
      model: locationCriterionsModel,
      bundleCountries,
      adGroupId: AdGroupNegativeReferenceId,
    }),
    ...isSearch ? {} : {
      ageRanges: [
        'EighteenToTwentyFive',
        'TwentyFiveToThirtyFive',
        'ThirtyFiveToFifty',
        'FiftyToSixtyFive',
        'SixtyFiveAndAbove',
        'Unknown',
      ],
      initialAgeRanges: [],
      negativeAgeRanges: [],
      genderTypes: [
        'Female',
        'Male',
        'Unknown',
      ],
      initialGenderTypes: [],
      negativeGenderTYpes: [],
      audiences: [],
      initialAudiences: [],
      initialIndustryTargets: [],
      industry: [],
      initialNegativeIndustryTargets: [],
      negativeIndustry: [],
      initialJobTargets: [],
      jobTargets: [],
      initialNegativeJobTargets: [],
      negativeJobTargets: [],
      initialCompanyNameTargets: [],
      companyNameTargets: [],
      initialNegativeCompanyNameTargets: [],
      negativeCompanyNameTargets: [],
      UseOptimizedTargeting: true,
      TargetSetting: 'Age;Gender',
    },
  };
  if (campaign.Id) {
    adgroup.Campaign = {
      Id: campaign.Id,
    };
  }
  return adgroup;
};

export const useTargetRepository = () => {
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const { state, send } = useWizardService();
  const { flow, createScenario } = getWizardData(state);

  const serverContext = useContext(ServerContext);
  const scenarioContext = useContext(ScenarioContext);
  const isPAAIMConsolidationEnabled = _.get(serverContext, ['DynamicPermissions', 'PAAIMConsolidation'], false);

  const activity = useContext(ActivityContext);
  const isSearch = isSearchShoppingCampaign(flow);
  const isCampaignCreate = createScenario === CREATE_SCENARIO.CREATE_CAMPAIGN;
  const { onStepLoadedLog, onStepSavedLog } = useWizardLog({ activity, wizardState: state, isSearch });

  const load = () => {
    onStepLoadedLog({ status: LOG_STATUS.LOAD_START });
    const savedCampaign = state.context.wizardData.campaign;
    const savedAdGroup = state.context.wizardData.adGroup;

    const adGroupId = savedAdGroup ? savedAdGroup.Id : null;
    const campaignId = savedCampaign ? savedCampaign.Id : null; // awf: 946842344, swf: 946842546

    const loadPromises = {
      bundleCountriesAndLocationCriterions: loadBundleCountries({ cid, aid, serverContext }),
      adGroup: savedAdGroup ? Promise.resolve(savedAdGroup) : Promise.resolve(),
      // saved campaigns are viewmodels
      campaign: savedCampaign ? Promise.resolve({
        ...viewModelToModelForCampaign({
          data: savedCampaign
        }),
        ...viewModelToModelForConversionGoals({
          data: savedCampaign
        })
      }) : loadCampaign(cid, aid, campaignId),
      dayTimeCriterions: readDayTimeCriterions({ cid, aid, campaignId }),
      locationCriterions: isSearch ? readLocationCriterions({ cid, aid, campaignId }) : (adGroupId ? readAdGroupLocationCriterions({ cid, aid, campaignId, adGroupId }) : Promise.resolve({ value: [] })),
      targetCriterions: isSearch ? readTargetingCriterions({ cid, aid, campaignId }) : (adGroupId ? readAdGroupTargetingCriterions({ cid, aid, campaignId, adGroupId }) : Promise.resolve({ value: [] })),
      audienceCriterions: isSearch ? readAudienceCriterions({ cid, aid, campaignId }) : (adGroupId ? readAdGroupAudienceCriterions({ cid, aid, campaignId, adGroupId }) : Promise.resolve({ value: [] })),
    };

    return Promise.props(loadPromises).then(({
      adGroup,
      campaign,
      locationCriterions,
      bundleCountriesAndLocationCriterions,
      dayTimeCriterions,
      targetCriterions,
      audienceCriterions,
    }) => {
      onStepLoadedLog({ status: LOG_STATUS.LOAD_SUCCESS });
      const adGroupData = adGroup ? modelToViewModelForAdGroup({
        adGroupModel: adGroup,
        campaignModel: campaign,
        i18n,
        isAudienceShoppingCampaign: !isSearch,
        locationCriterionsModel: !isSearch && locationCriterions ? locationCriterions.value : [],
        bundleCountries: !isSearch ? bundleCountriesAndLocationCriterions.bundleCountries : [],
        targetCriterionsModel: !isSearch && targetCriterions ? targetCriterions.value : [],
        audienceCriterionsModel: !isSearch && audienceCriterions ? audienceCriterions.value : [],
      }) : { data: getAdGroup({ campaign, locationCriterionsModel: locationCriterions.value, bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries }), isValid: true, isDirty: false };
      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
        locationCriterionsModel: isSearch && locationCriterions ? locationCriterions.value : [],
        bundleCountries: isSearch ? bundleCountriesAndLocationCriterions.bundleCountries : [],
        targetCriterionsModel: isSearch && targetCriterions ? targetCriterions.value : [],
        audienceCriterionsModel: isSearch && audienceCriterions ? audienceCriterions.value : [],
      });

      // Only set the default value for AdRotation if it not in the creation wizard
      if (!isCampaignCreate) {
        adGroupData.data.AdRotation = adGroupData.data.AdRotation || 'OptimizeForClicks';
      }

      return {
        adGroup: adGroupData.data,
        campaign: campaignData.data,
        ...bundleCountriesAndLocationCriterions,
        ...getErrorsPlaceholders(),
      }
    });
  };

  const save = ({ data }) => {
    onStepSavedLog({ status: LOG_STATUS.SAVE_START });
    const {
      campaign,
      adGroup,
      bundleCountries,
      bundleLocationCriterions,
    } = data;

    const saveCampaignPromise = saveCampaign({
      campaign: { data: campaign },
      viewModelToBulkModelForAdSchedules,
      updateCampaignStatus: isCampaignCreate,
      bundleLocationCriterions: isSearch ? bundleLocationCriterions : null,
      bundleCountries: isSearch ? bundleCountries : null,
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
      i18n,
    });

    const saveAdGroupPromise = saveAdGroup({
      adGroup: { data: adGroup },
      campaign,
      bundleCountries: isSearch ? null : bundleCountries,
      bundleLocationCriterions: isSearch ? null : bundleLocationCriterions,
      saveAdGroupCriterions: !isSearch,
      isProductGroupAllowedForAudienceCampaign: isPAAIMConsolidationEnabled,
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveAdGroup'),
      i18n,
      serverContext,
    });

    const savePromises = [saveCampaignPromise, saveAdGroupPromise];

    return new Promise((resolve, reject) => {
      allSettledPromiseWrapper(savePromises).then((results) => {
        const allSuccess = _.every(results, result => result.status === 'fulfilled');

        if (allSuccess) {

          const campaignData = results[0].value.campaign.data;
          const adGroupData = results[1].value.adGroup.data;

          onStepSavedLog({ status: LOG_STATUS.SAVE_SUCCESS, msg: `Status: ${campaignData.Status}, Id: ${campaignData.Id}` });
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
            campaign: campaignData,
            adGroup: adGroupData,
          });

          return resolve({
            adGroup: adGroupData,
            campaign: campaignData,
            ...getErrorsPlaceholders(),
          });
        }

        const errors = _.map(results, (result) => {
          if (result.status === 'fulfilled') {
            return null;
          }

          const { serverErrors = {} } = result.reason;

          return serverErrors;
        });
        onStepSavedLog({ status: LOG_STATUS.SAVE_FAIL, msg: results });
        return reject({ error: _.flatten(errors.filter(val => val !== null)) });
      });
    });
  };

  return {
    load,
    save,
  };
};